import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row, Select, Table, message } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import SuperAdminAPI from '../../../services/SuperAdminAPI';

const { Option } = Select;

const UsageAnalyticsPanel = () => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(undefined);
  const [days, setDays] = useState(7);

  const loadTenants = async () => {
    try {
      const data = await SuperAdminAPI.listTenants();
      setTenants(data || []);
      if (!selectedTenant && data && data.length > 0) {
        setSelectedTenant(data[0]._id);
      }
    } catch (error) {
      console.error('UsageAnalyticsPanel: failed to load tenants', error);
      message.error('Failed to load tenants');
      setTenants([]);
    }
  };

  const loadSummary = async () => {
    if (!selectedTenant) return;
    setLoading(true);
    try {
      const data = await SuperAdminAPI.getUsageSummary(selectedTenant, days);
      setSummary(data);
    } catch (error) {
      console.error('UsageAnalyticsPanel: failed to load summary', error);
      message.error('Failed to load usage summary');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    if (!selectedTenant) return;
    setEventsLoading(true);
    try {
      const data = await SuperAdminAPI.getUsageEvents(selectedTenant, {
        limit: 20,
        feature: selectedFeature || undefined,
      });
      setEvents(data?.items || []);
    } catch (error) {
      console.error('UsageAnalyticsPanel: failed to load events', error);
      message.error('Failed to load usage events');
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    loadSummary();
  }, [selectedTenant, days]);

  useEffect(() => {
    loadEvents();
  }, [selectedTenant, selectedFeature]);

  const featureRows = useMemo(() => {
    if (!summary?.byFeature) return [];
    return Object.entries(summary.byFeature).map(([feature, values]) => ({
      feature,
      units: values.units,
      costUnits: values.costUnits,
    }));
  }, [summary]);

  const dailyChart = useMemo(() => {
    if (!summary?.meters) return [];
    const grouped = {};
    summary.meters.forEach(m => {
      const key = m.dateKey || 'unknown';
      if (!grouped[key]) grouped[key] = { date: key, units: 0, costUnits: 0 };
      grouped[key].units += m.units || 0;
      grouped[key].costUnits += m.costUnits || 0;
    });
    return Object.values(grouped).sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [summary]);

  const featureColumns = [
    { title: 'Feature', dataIndex: 'feature', key: 'feature' },
    { title: 'Units', dataIndex: 'units', key: 'units' },
    { title: 'Cost Units', dataIndex: 'costUnits', key: 'costUnits' },
  ];

  const eventColumns = [
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => (value ? new Date(value).toLocaleString() : '—'),
    },
    { title: 'Feature', dataIndex: 'feature', key: 'feature' },
    { title: 'Units', dataIndex: 'units', key: 'units' },
    { title: 'Cost Units', dataIndex: 'costUnits', key: 'costUnits' },
    { title: 'Source', dataIndex: 'source', key: 'source' },
  ];

  const featureOptions = Object.keys(summary?.byFeature || {});

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card
          title="Usage Analytics"
          extra={
            <div style={{ display: 'flex', gap: 12 }}>
              <Select value={days} onChange={setDays} style={{ width: 120 }}>
                <Option value={7}>Last 7 days</Option>
                <Option value={14}>Last 14 days</Option>
                <Option value={30}>Last 30 days</Option>
              </Select>
              <Select
                value={selectedTenant}
                onChange={setSelectedTenant}
                placeholder="Select tenant"
                style={{ width: 240 }}
              >
                {tenants.map(tenant => (
                  <Option key={tenant._id} value={tenant._id}>
                    {tenant.name || tenant.slug || tenant._id}
                  </Option>
                ))}
              </Select>
              <Select
                value={selectedFeature}
                onChange={setSelectedFeature}
                placeholder="Filter feature"
                allowClear
                style={{ width: 220 }}
              >
                {featureOptions.map(feature => (
                  <Option key={feature} value={feature}>
                    {feature}
                  </Option>
                ))}
              </Select>
              <Button onClick={loadSummary} loading={loading}>
                Refresh
              </Button>
            </div>
          }
        >
          <Row gutter={[16, 16]}>
            <Col span={14}>
              <Card title="Daily Usage" size="small">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={dailyChart}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="units" stroke="#1890ff" name="Units" />
                    <Line type="monotone" dataKey="costUnits" stroke="#faad14" name="Cost Units" />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col span={10}>
              <Card title="Feature Usage" size="small">
                <Table
                  rowKey="feature"
                  loading={loading}
                  columns={featureColumns}
                  dataSource={featureRows}
                  pagination={{ pageSize: 6 }}
                />
              </Card>
            </Col>
          </Row>
        </Card>
      </Col>
      <Col span={24}>
        <Card title="Recent Metered Events">
          <Table
            rowKey={record => record._id}
            loading={eventsLoading}
            columns={eventColumns}
            dataSource={events}
            pagination={{ pageSize: 8 }}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default UsageAnalyticsPanel;
